// ══════════════════════════════════════════════════════
//  Helpers partagés du tableau de bord (Supervision, Tour de contrôle,
//  Vue d'ensemble, Serveurs, Réseau & stockage).
//  Aucune donnée inventée ici : tout est calculé à partir de ce que le
//  backend renvoie déjà (NAS, passerelles UniFi, serveurs Atera).
// ══════════════════════════════════════════════════════

export const REFRESH_INTERVAL_MS = 30_000;

// Seuils d'occupation disque des serveurs — mêmes seuils que la fiche ticket
// (AssetContextCard) pour rester cohérent partout dans l'app.
export const DISK_WARNING  = 70;
export const DISK_CRITICAL = 85;

// Une synchro NAS/UniFi tourne toutes les 5 min côté backend : au-delà de
// 15 min sans nouvelle donnée, l'appareil n'est plus réellement surveillé.
export const STALE_SYNC_MS = 15 * 60 * 1000;

/** Les 3 niveaux de santé, du plus grave (rang 0) au plus sain. */
export const HEALTH = {
  CRITICAL: { key: "CRITICAL", label: "Critique",      long: "Critique",      color: "var(--danger)",  bg: "var(--danger-dim)",  rank: 0 },
  WARNING:  { key: "WARNING",  label: "Avertissement", long: "Avertissement", color: "var(--warning)", bg: "var(--warning-dim)", rank: 1 },
  OK:       { key: "OK",       label: "OK",            long: "Opérationnel",  color: "var(--success)", bg: "var(--success-dim)", rank: 2 },
};

export const health = (status) => HEALTH[status] || HEALTH.OK;

// ─────────────────────────────────────────────
//  TEMPS
// ─────────────────────────────────────────────

export function timeAgo(iso) {
  if (!iso) return "jamais";
  const diff = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  return agoFromSeconds(diff);
}

/** Epoch (secondes) → "il y a 3 h". */
export function timeAgoEpoch(epochSec) {
  if (!epochSec) return "";
  const diff = Math.max(0, Math.floor(Date.now() / 1000 - epochSec));
  return agoFromSeconds(diff);
}

function agoFromSeconds(diff) {
  if (diff < 60)    return "à l'instant";
  if (diff < 3600)  return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
  return `il y a ${Math.floor(diff / 86400)} j`;
}

export function isStale(iso) {
  if (!iso) return true;
  return Date.now() - new Date(iso).getTime() > STALE_SYNC_MS;
}

// ─────────────────────────────────────────────
//  FORMATAGE
// ─────────────────────────────────────────────

/** 12700 Go → "12,4 To" ; 800 Go → "800 Go". */
export function formatStorage(gb) {
  if (gb == null) return "—";
  if (gb >= 1024) return `${(gb / 1024).toLocaleString("fr-FR", { maximumFractionDigits: 1 })} To`;
  return `${gb} Go`;
}

/** Couleur d'une jauge de stockage (NAS : mêmes seuils que le backend, 80 / 90 %). */
export function storageColor(pct) {
  if (pct == null) return "var(--text-3)";
  return pct >= 90 ? HEALTH.CRITICAL.color : pct >= 80 ? HEALTH.WARNING.color : HEALTH.OK.color;
}

/** Couleur d'une jauge disque de serveur (70 / 85 %). */
export function diskColor(pct) {
  return pct >= DISK_CRITICAL ? HEALTH.CRITICAL.color : pct >= DISK_WARNING ? HEALTH.WARNING.color : HEALTH.OK.color;
}

// ─────────────────────────────────────────────
//  STATUT EFFECTIF DES APPAREILS
// ─────────────────────────────────────────────

/**
 * Statut affiché d'un NAS ou d'une passerelle. Si la dernière synchro a
 * échoué (identifiants refusés, appareil injoignable…), le statut stocké
 * date d'avant l'échec et reste "OK" : l'appareil n'est alors plus surveillé,
 * ce qui mérite au minimum un avertissement visible.
 */
export function deviceStatus(d) {
  const base = d.status || "OK";
  if (d.lastError && base === "OK") return "WARNING";
  return base;
}

export function diskInfo(asset) {
  if (!asset.diskTotalGb || asset.diskUsedGb == null) return null;
  const pct = Math.min(100, Math.round((asset.diskUsedGb / asset.diskTotalGb) * 100));
  return { pct, color: diskColor(pct) };
}

/** Statut de santé d'un serveur Atera (hors ligne = critique). */
export function serverStatus(asset) {
  if (asset.status !== "ONLINE") return "CRITICAL";
  const disk = diskInfo(asset);
  if (disk && disk.pct >= DISK_CRITICAL) return "CRITICAL";
  if (disk && disk.pct >= DISK_WARNING)  return "WARNING";
  return "OK";
}

/** Tri du plus urgent au plus calme, puis par nom. */
export function byUrgency(getStatus, getName) {
  return (a, b) => {
    const d = health(getStatus(a)).rank - health(getStatus(b)).rank;
    return d !== 0 ? d : (getName(a) || "").localeCompare(getName(b) || "");
  };
}

export const nasStatusOf     = (n) => deviceStatus(n);
export const gatewayStatusOf = (g) => deviceStatus(g);

export function worstStatus(statuses) {
  return statuses.reduce((worst, s) => (health(s).rank < health(worst).rank ? s : worst), "OK");
}

export function countStatuses(statuses) {
  const c = { OK: 0, WARNING: 0, CRITICAL: 0 };
  statuses.forEach(s => { if (c[s] !== undefined) c[s]++; });
  return c;
}

// ─────────────────────────────────────────────
//  SAUVEGARDES
// ─────────────────────────────────────────────

export function backupCounts(jobs = []) {
  return countStatuses(jobs.map(j => j.status));
}

/**
 * Résumé des sauvegardes d'un NAS :
 *  - latest  : la tâche exécutée le plus récemment (succès ou non)
 *  - lastOk  : la dernière tâche réussie
 * Se base sur lastRunEpoch ; une tâche sans horodatage exploitable est ignorée
 * ici (elle reste affichée dans la liste, avec son libellé texte).
 */
export function backupSummary(jobs = []) {
  const dated = jobs.filter(j => j.lastRunEpoch);
  const latest = dated.reduce((a, j) => (!a || j.lastRunEpoch > a.lastRunEpoch ? j : a), null);
  const lastOk = dated.filter(j => j.status === "OK")
    .reduce((a, j) => (!a || j.lastRunEpoch > a.lastRunEpoch ? j : a), null);
  return { latest, lastOk, counts: backupCounts(jobs) };
}

// ─────────────────────────────────────────────
//  INCIDENTS — dérivés des données réelles (rien n'est stocké)
// ─────────────────────────────────────────────

/**
 * Liste à plat de tout ce qui demande attention, du plus grave au plus
 * calme. Sert au bandeau d'incidents (Tour de contrôle) et à la liste
 * "Alertes & incidents actifs" (Vue d'ensemble).
 */
export function buildIncidents({ nas = [], gateways = [], servers = [] }) {
  const out = [];
  const push = (level, kind, id, device, company, title, detail) =>
    out.push({ key: `${kind}-${id}-${out.length}`, level, kind, id, device, company, title, detail });

  nas.forEach(n => {
    const nm = n.name, co = n.companyName;
    if (n.lastError) push("WARNING", "nas", n.id, nm, co, "Synchronisation en échec", n.lastError);
    else if (isStale(n.lastSyncedAt) && n.lastSyncedAt) push("WARNING", "nas", n.id, nm, co, "Données non rafraîchies", `Dernière synchro ${timeAgo(n.lastSyncedAt)}`);

    if (n.raidStatus && n.raidStatus.toLowerCase() !== "normal")
      push("CRITICAL", "nas", n.id, nm, co, "RAID dégradé", `État du volume : ${n.raidStatus}`);
    (n.disks || []).filter(d => d.status === "CRITICAL").forEach(d =>
      push("CRITICAL", "nas", n.id, nm, co, `Disque baie ${d.bay} défaillant`, d.model || "Disque en échec"));

    const pct = n.storagePercent;
    if (pct != null && pct >= 90) push("CRITICAL", "nas", n.id, nm, co, "Stockage presque plein", `${pct} % utilisé (${formatStorage(n.storageUsedGb)} / ${formatStorage(n.storageTotalGb)})`);
    else if (pct != null && pct >= 80) push("WARNING", "nas", n.id, nm, co, "Stockage élevé", `${pct} % utilisé`);

    (n.backupJobs || []).forEach(j => {
      // Une sauvegarde en échec fait passer le NAS en avertissement (jamais en
      // critique à elle seule — même règle que le calcul de statut côté serveur).
      if (j.status === "CRITICAL") push("WARNING", "nas", n.id, nm, co, `Sauvegarde en échec — ${j.jobName}`, j.lastRunLabel ? `Dernière exécution ${j.lastRunLabel}` : (j.rawStatus || ""));
      else if (j.status === "WARNING") push("WARNING", "nas", n.id, nm, co, `Sauvegarde partielle — ${j.jobName}`, j.lastRunLabel ? `Dernière exécution ${j.lastRunLabel}` : (j.rawStatus || ""));
    });
  });

  gateways.forEach(g => {
    const nm = g.name, co = g.companyName;
    if (g.lastError) push("WARNING", "udm", g.id, nm, co, "Synchronisation en échec", g.lastError);
    else if (g.lastSyncedAt && isStale(g.lastSyncedAt)) push("WARNING", "udm", g.id, nm, co, "Données non rafraîchies", `Dernière synchro ${timeAgo(g.lastSyncedAt)}`);
    if (g.status === "CRITICAL") push("CRITICAL", "udm", g.id, nm, co, "Connexion WAN hors ligne", `Dernier contact ${timeAgo(g.lastSeenAt)}`);
    else if (g.status === "WARNING") push("WARNING", "udm", g.id, nm, co, "Connexion dégradée", g.latencyMs != null ? `Latence ${g.latencyMs} ms` : "Qualité WAN dégradée");
  });

  servers.forEach(a => {
    const nm = a.hostname, co = a.companyName;
    if (a.status !== "ONLINE") { push("CRITICAL", "server", a.id, nm, co, "Serveur hors ligne", `Agent : ${a.lastSeenAt ? timeAgo(a.lastSeenAt) : "jamais connecté"}`); return; }
    const d = diskInfo(a);
    if (d && d.pct >= DISK_CRITICAL) push("CRITICAL", "server", a.id, nm, co, "Disque presque plein", `${d.pct} % utilisé`);
    else if (d && d.pct >= DISK_WARNING) push("WARNING", "server", a.id, nm, co, "Disque élevé", `${d.pct} % utilisé`);
  });

  return out.sort((a, b) => health(a.level).rank - health(b.level).rank);
}

export const KIND_LABEL = { nas: "NAS", udm: "UDM", server: "Serveur" };
export const KIND_ICON  = { nas: "bi-hdd-network", udm: "bi-router", server: "bi-pc-display-horizontal" };

/** Regroupe tous les appareils par entreprise cliente. */
export function groupByCompany({ nas = [], gateways = [], servers = [] }) {
  const map = new Map();
  const slot = (name) => {
    const key = name || "Sans entreprise";
    if (!map.has(key)) map.set(key, { company: key, nas: [], gateways: [], servers: [] });
    return map.get(key);
  };
  nas.forEach(n => slot(n.companyName).nas.push(n));
  gateways.forEach(g => slot(g.companyName).gateways.push(g));
  servers.forEach(s => slot(s.companyName).servers.push(s));
  const groups = [...map.values()].map(g => ({
    ...g,
    status: worstStatus([
      ...g.nas.map(nasStatusOf),
      ...g.gateways.map(gatewayStatusOf),
      ...g.servers.map(serverStatus),
    ]),
  }));
  return groups.sort(byUrgency(g => g.status, g => g.company));
}


// ─────────────────────────────────────────────
//  TICKETS (vue colonnes)
// ─────────────────────────────────────────────

export const TICKET_STATUS_LABEL = { OPEN: "Ouvert", IN_PROGRESS: "En cours", PENDING: "En attente", RESOLVED: "Résolu", CLOSED: "Fermé" };
export const TICKET_PRIORITY_LABEL = { CRITICAL: "Critique", HIGH: "Haute", MEDIUM: "Moyenne", LOW: "Basse" };
export const TICKET_ACTIVE = ["OPEN", "IN_PROGRESS", "PENDING"];

// Mêmes seuils SLA que la page Tickets (heures écoulées depuis la création)
const SLA = { CRITICAL: { warning: 2, late: 4 }, HIGH: { warning: 4, late: 8 }, MEDIUM: { warning: 12, late: 24 }, LOW: { warning: 24, late: 48 } };

/** "late" | "warning" | null — null pour un ticket résolu/fermé ou sans date. */
export function ticketSla(t) {
  if (!TICKET_ACTIVE.includes(t.status) || !t.createdAt) return null;
  const th = SLA[t.priority] || SLA.LOW;
  const hours = (Date.now() - new Date(t.createdAt).getTime()) / 3_600_000;
  if (hours >= th.late) return "late";
  if (hours >= th.warning) return "warning";
  return null;
}

/**
 * Niveau d'urgence d'un ticket (même échelle que les appareils) :
 * actif + critique → CRITICAL ; actif + haute (ou SLA dépassé) → WARNING ; sinon OK.
 */
export function ticketLevel(t) {
  if (!TICKET_ACTIVE.includes(t.status)) return "OK";
  if (t.priority === "CRITICAL") return "CRITICAL";
  if (t.priority === "HIGH" || ticketSla(t) === "late") return "WARNING";
  return "OK";
}